import { Injectable } from '@angular/core';
import { AppKey, AppKeyInterface } from '@galaxy/marketplace-apps';
import { TranslateService } from '@ngx-translate/core';
import { WorkflowStepInterface } from '@vendasta/automata';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ANY } from './constants';
import { ANY_ADDON, ANY_EDITION, AppsService } from '../../../../../data-services/apps.service';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example {@link AccountGroupCreatedService}
 */
@Injectable({ providedIn: 'root' })
export class ProductActivationService {
  constructor(private readonly appsService: AppsService, private readonly translateService: TranslateService) {}

  getProductName(
    partnerId: string,
    marketId: string,
    appKey: AppKeyInterface,
    includeNotEnabled: boolean,
    addon?: AppKeyInterface,
    isTrial: boolean = null,
  ): Observable<string> {
    let trialTag = '';
    if (isTrial === true) {
      trialTag = ' - ' + this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.TRIAL');
    } else if (isTrial === false) {
      trialTag = ' - ' + this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.NO_TRIAL');
    }
    const productName$ = this.appsService.getProductName(appKey, includeNotEnabled).pipe(map((a) => a + trialTag));

    if (
      !!addon &&
      addon.appId !== '' &&
      addon.appId !== ANY &&
      addon.appId !== ANY_ADDON &&
      addon.appId !== ANY_EDITION
    ) {
      // Verifies we aren't in any case where addons aren't set to any of the predefined values meaning it must be an addon ID
      return combineLatest([productName$, this.appsService.getAddons(appKey.appId)]).pipe(
        map(
          ([productName, addons]) =>
            productName.replace(
              ` (${this.translateService
                .instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.ANY_EDITION')
                .toLowerCase()})`,
              '',
            ) + ` | ${addons.find((a) => a.addonId === addon.appId).title}`,
        ),
      );
    }
    if (!!addon && addon.appId === ANY_ADDON) {
      // Verfifies if it's not a specific ID then it must be set for any addon
      return productName$.pipe(
        map(
          (n) =>
            n.replace(
              ` (${this.translateService
                .instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.ANY_EDITION')
                .toLowerCase()})`,
              '',
            ) +
            ` (${this.translateService.instant(
              'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.ANY_ADDON',
            )})`.toLowerCase(),
        ),
      );
    } else if (!!addon && addon.appId === ANY && appKey.editionId === ANY_EDITION) {
      // Verfifies if it's not a specific ID or any addon then it must be set for any edition
      return productName$.pipe(
        map(
          (n) =>
            n.replace(
              ` (${this.translateService
                .instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.ANY_EDITION')
                .toLowerCase()})`,
              '',
            ) +
            ` (${this.translateService
              .instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.EDITION_OR_ADDON')
              .toLowerCase()})`,
        ),
      );
    }
    return productName$;
  }

  getAppKeyFromStep(step: WorkflowStepInterface): AppKey {
    const comparisonRules = JSON.parse(step?.choices?.rules[0]?.value || '[]');

    let appID = '';
    let editionID = ANY_EDITION;

    for (const rule of comparisonRules) {
      if (rule['variable_path'].includes('.app_id')) {
        appID = rule.value;
      }
      if (rule['variable_path'].includes('.edition_id')) {
        // note that "Pro" editions will have an empty string for its value
        editionID = rule.value;
      }
    }

    return new AppKey({
      appId: appID,
      editionId: editionID,
    });
  }

  getTrialFromStep(step: WorkflowStepInterface): boolean {
    const comparisonRules = JSON.parse(step?.choices?.rules[0]?.value || '[]');
    let isTrial: boolean = null;

    for (const rule of comparisonRules) {
      if (rule['variable_path'].includes('.is_trial')) {
        isTrial = rule.value;
      }
    }
    return isTrial;
  }

  getAddonFromStep(step: WorkflowStepInterface): AppKey {
    const comparisonRules = JSON.parse(step?.choices?.rules[0]?.value || '[]');
    let addonId = '';

    for (const rule of comparisonRules) {
      if (rule['variable_path'].includes('.addon_id')) {
        addonId = rule.value;
      }
    }
    return new AppKey({ appId: addonId });
  }
}
